import { useEffect, useState, useCallback, useRef } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Briefcase, ShieldAlert, Camera, Upload, FileSignature, Plus, CheckCircle2,
  CircleDollarSign, ChevronDown, ClipboardCheck,
} from "lucide-react";

const usd = (n) => (n === null || n === undefined || n === "" ? "—"
  : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const STATE = {
  draft: ["bg-sand-200 text-ink/65", "Being set up"],
  pending_paperwork: ["bg-amber-50 text-amber-800", "Waiting on your paperwork"],
  active: ["bg-emerald-50 text-emerald-700", "Cleared to start"],
  in_progress: ["bg-indigo-50 text-indigo-700", "In progress"],
  complete: ["bg-sand-200 text-ink/55", "Complete"],
};
const PAPER = { coi: "your certificate of insurance", w9: "your W-9", agreement: "a signed agreement" };

function Job({ token, id, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [co, setCo] = useState({ description: "", amount: "" });
  const [inv, setInv] = useState({ inv_type: "final", number: "", amount: "" });
  const photoRef = useRef(null);
  const contractRef = useRef(null);
  const invRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/fs/vendor/${token}/work-orders/${id}`);
    setD(data);
  }, [token, id]);
  useEffect(() => { load().catch(() => toast.error("Couldn't open that job")); }, [load]);

  if (!d) return <div className="grid h-24 place-items-center"><Loader2 className="h-4 w-4 animate-spin text-ink/30" /></div>;
  const w = d.work_order;
  const live = ["active", "in_progress"].includes(w.status);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e, "Try again")); }
    setBusy(false);
  };

  return (
    <div className="space-y-4 border-t border-sand-100 px-5 py-4" data-testid={`vwo-detail-${id}`}>
      {(w.paperwork_missing || []).length > 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="vwo-gate">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="min-w-0 text-xs leading-relaxed text-amber-900">
            <b>You can't start yet.</b> We still need {(w.paperwork_missing || []).map((k) => PAPER[k] || k).join(" and ")}.
            {w.paperwork_due && <> The community is expecting it by <b>{String(w.paperwork_due).slice(0, 10)}</b>.</>}
            {" "}Upload it higher up this page and this job clears itself.
          </div>
        </div>
      )}

      {(w.scope || (w.scope_files || []).length > 0) && (
        <div data-testid="vwo-scope">
          <div className="mb-1 text-xs font-bold uppercase tracking-wider text-ink/40">
            What we agreed{w.scope_revision ? ` · scope v${w.scope_revision}` : ""}{w.scope_updated_at ? ` · updated ${String(w.scope_updated_at).slice(0, 10)}` : ""}
          </div>
          {w.scope && <p className="whitespace-pre-line rounded-xl bg-sand-50 px-4 py-3 text-sm leading-relaxed text-ink/75">{w.scope}</p>}
          {(w.scope_files || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {w.scope_files.map((f) => (
                <a key={f.id} href={f.url} target="_blank" rel="noreferrer" data-testid={`vwo-scope-file-${f.id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-sand-300 px-2.5 py-1 text-xs font-semibold text-ink/70 hover:text-ink">
                  <ClipboardCheck className="h-3.5 w-3.5" /> {f.name}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-3 text-sm">
        <div className="rounded-xl bg-sand-50 px-3 py-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-ink/40">Your price</div>
          <div className="font-bold text-ink">{w.price_pending ? <span className="text-amber-700">to be agreed</span> : usd(w.amount)}</div>
        </div>
        {w.deposit ? (
          <div className="rounded-xl bg-sand-50 px-3 py-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-ink/40">Deposit</div>
            <div className="font-bold text-ink">{usd(w.deposit)}</div>
          </div>
        ) : null}
        {w.start_window ? (
          <div className="rounded-xl bg-sand-50 px-3 py-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-ink/40">Start window</div>
            <div className="font-bold text-ink">{w.start_window}</div>
          </div>
        ) : null}
      </div>

      {/* contract */}
      {(d.contract || {}).path && (d.contract.status !== "skipped") && (
        <div className="rounded-xl border border-sand-200 px-4 py-3" data-testid="vwo-contract">
          <div className="inline-flex items-center gap-1.5 text-sm font-bold text-ink">
            <FileSignature className="h-4 w-4 text-ink/40" /> Contract
          </div>
          <p className="mt-0.5 text-xs text-ink/55">
            {{ awaiting_vendor: "Send us your contract and we'll countersign it.",
              vendor_uploaded: "We've got yours — the community is reading it now.",
              out_for_signature: "It's out for signature. Check your texts and email for your link.",
              signed: "Signed by everyone. Nothing else needed." }[d.contract.status]
              || "We'll send it over shortly."}
          </p>
          {d.contract.file_url && (
            <a href={d.contract.file_url} target="_blank" rel="noreferrer"
              className="mt-1 inline-block text-xs font-bold text-ink/60 underline">open the file</a>
          )}
          {["awaiting_vendor", "none"].includes(d.contract.status) && (
            <>
              <button data-testid="vwo-contract-upload" disabled={busy} onClick={() => contractRef.current?.click()}
                className="mt-2 inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2 text-sm font-bold text-ink/70 disabled:opacity-40">
                <Upload className="h-4 w-4" /> Send your contract (PDF)
              </button>
              <input ref={contractRef} type="file" accept="application/pdf" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                const fd = new FormData(); fd.append("file", file);
                await run(() => api.post(`/fs/vendor/${token}/work-orders/${id}/contract`, fd),
                  "Sent — the community will read it and countersign");
                e.target.value = "";
              }} />
            </>
          )}
        </div>
      )}

      {/* punch list */}
      {(w.punch_list || []).length > 0 && (
        <div data-testid="vwo-punch">
          <div className="mb-1 text-xs font-bold uppercase tracking-wider text-ink/40">
            <ClipboardCheck className="mr-1 inline h-3.5 w-3.5" /> Punch list
          </div>
          {w.punch_list.map((p) => (
            <div key={p.id} className="mb-1.5 flex items-center justify-between gap-2 rounded-xl bg-sand-50 px-4 py-2.5">
              <span className={`min-w-0 flex-1 text-sm ${p.done ? "text-ink/35 line-through" : "text-ink/80"}`}>{p.text}</span>
              {!p.done && (
                <button data-testid={`vwo-punch-done-${p.id}`} disabled={busy}
                  onClick={() => run(() => api.post(`/fs/vendor/${token}/work-orders/${id}/punch/${p.id}/done`),
                    "Marked done")}
                  className="shrink-0 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40">
                  Done
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {live && (
        <div data-testid="vwo-progress-form">
          <div className="mb-1 text-xs font-bold uppercase tracking-wider text-ink/40">Post an update</div>
          <textarea data-testid="vwo-note" rows={2} value={note} placeholder="What got done today?"
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none" />
          <div className="mt-2 flex gap-2">
            <button data-testid="vwo-progress-send" disabled={busy || !note.trim()} onClick={() => run(async () => {
              const fd = new FormData(); fd.append("note", note);
              await api.post(`/fs/vendor/${token}/work-orders/${id}/progress`, fd);
              setNote("");
            }, "Sent")}
              className="rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">Send</button>
            <button data-testid="vwo-photo" disabled={busy} onClick={() => photoRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2 text-sm font-bold text-ink/70 disabled:opacity-40">
              <Camera className="h-4 w-4" /> Add a photo
            </button>
            <input ref={photoRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={async (e) => {
              const file = e.target.files?.[0]; if (!file) return;
              const fd = new FormData(); fd.append("file", file); fd.append("note", note || "Progress photo");
              await run(() => api.post(`/fs/vendor/${token}/work-orders/${id}/progress`, fd), "Photo sent");
              setNote(""); e.target.value = "";
            }} />
          </div>
        </div>
      )}

      {(w.progress || []).length > 0 && (
        <div>
          <div className="mb-1 text-xs font-bold uppercase tracking-wider text-ink/40">Your updates</div>
          {w.progress.slice().reverse().slice(0, 6).map((p) => (
            <div key={p.id} className="mb-1.5 rounded-xl bg-sand-50 px-4 py-2.5 text-sm text-ink/70">
              {p.note}
              {(p.photo_urls || []).length > 0 && (
                <div className="mt-1.5 flex gap-1.5">
                  {p.photo_urls.map((u, i) => <img key={i} src={u} alt="" className="h-14 w-14 rounded-lg object-cover" />)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {live && (
        <div data-testid="vwo-co-form">
          <div className="mb-1 text-xs font-bold uppercase tracking-wider text-ink/40">Found extra work?</div>
          <p className="mb-2 text-xs leading-relaxed text-ink/50">
            Send a change order and wait for the OK before you do it — that's what protects you from an
            unpaid invoice later.
          </p>
          <div className="flex flex-wrap gap-2">
            <input data-testid="vwo-co-desc" value={co.description} placeholder="What needs doing"
              onChange={(e) => setCo({ ...co, description: e.target.value })}
              className="min-w-0 flex-1 rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none" />
            <input data-testid="vwo-co-amount" type="number" min="0" step="0.01" value={co.amount} placeholder="Amount"
              onChange={(e) => setCo({ ...co, amount: e.target.value })}
              className="w-28 rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none" />
            <button data-testid="vwo-co-send" disabled={busy || !co.description.trim() || !co.amount}
              onClick={() => run(async () => {
                await api.post(`/fs/vendor/${token}/work-orders/${id}/change-order`, co);
                setCo({ description: "", amount: "" });
              }, "Sent for approval")}
              className="rounded-xl bg-ink px-3 py-2 text-sm font-bold text-white disabled:opacity-40">
              <Plus className="h-4 w-4" />
            </button>
          </div>
          {(w.change_orders || []).map((c) => (
            <div key={c.id} className="mt-1.5 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-sand-50 px-4 py-2 text-xs">
              <span className="text-ink/65">{usd(c.amount)} · {c.description}</span>
              <span className={`rounded-full px-2 py-0.5 font-bold uppercase ${
                c.status === "approved" ? "bg-emerald-50 text-emerald-700"
                  : c.status === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
                {c.status === "board_review" ? "with the board" : c.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {live || w.status === "complete" ? (
        <div data-testid="vwo-invoice-form">
          <div className="mb-1 text-xs font-bold uppercase tracking-wider text-ink/40">
            <CircleDollarSign className="mr-1 inline h-3.5 w-3.5" /> Send an invoice
          </div>
          <div className="flex flex-wrap gap-2">
            <select data-testid="vwo-inv-type" value={inv.inv_type} onChange={(e) => setInv({ ...inv, inv_type: e.target.value })}
              className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm outline-none">
              <option value="deposit">Deposit</option>
              <option value="progress">Progress</option>
              <option value="final">Final</option>
            </select>
            <input data-testid="vwo-inv-number" value={inv.number} placeholder="Your invoice #"
              onChange={(e) => setInv({ ...inv, number: e.target.value })}
              className="w-32 rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none" />
            <input data-testid="vwo-inv-amount" type="number" min="0" step="0.01" value={inv.amount} placeholder="Amount"
              onChange={(e) => setInv({ ...inv, amount: e.target.value })}
              className="w-28 rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none" />
            <button data-testid="vwo-inv-file" disabled={busy} onClick={() => invRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-2 text-sm font-bold text-ink/70 disabled:opacity-40">
              <Upload className="h-4 w-4" /> Attach & send
            </button>
            <button data-testid="vwo-inv-send" disabled={busy || !inv.amount} onClick={() => run(async () => {
              const fd = new FormData();
              Object.entries(inv).forEach(([k, v]) => fd.append(k, v));
              await api.post(`/fs/vendor/${token}/work-orders/${id}/invoice`, fd);
              setInv({ inv_type: "final", number: "", amount: "" });
            }, "Sent to the community")}
              className="rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">Send</button>
            <input ref={invRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={async (e) => {
              const file = e.target.files?.[0]; if (!file) return;
              const fd = new FormData();
              Object.entries(inv).forEach(([k, v]) => fd.append(k, v));
              fd.append("file", file);
              await run(async () => {
                const { data } = await api.post(`/fs/vendor/${token}/work-orders/${id}/invoice`, fd);
                if (data.extracted?.total) toast.info(`We read ${usd(data.extracted.total)} off your PDF`);
                setInv({ inv_type: "final", number: "", amount: "" });
              }, "Sent to the community");
              e.target.value = "";
            }} />
          </div>
          {(d.invoices || []).map((i) => (
            <div key={i.id} className="mt-1.5 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-sand-50 px-4 py-2 text-xs">
              <span className="text-ink/65">{usd(i.amount)} · {i.type}{i.number ? ` · #${i.number}` : ""}</span>
              <span className="font-semibold text-ink/50">{i.status}{i.reject_reason ? ` — ${i.reject_reason}` : ""}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function VendorWorkOrders({ token }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/fs/vendor/${token}/work-orders`);
    setRows(data.work_orders || []);
  }, [token]);
  useEffect(() => { load().catch(() => setRows([])); }, [load]);

  if (!rows) return null;
  if (!rows.length) return null;

  return (
    <div className="mt-8" data-testid="vendor-work-orders">
      <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
        <Briefcase className="h-3.5 w-3.5" /> Your jobs
      </div>
      {rows.map((w) => {
        const [cls, label] = STATE[w.status] || ["bg-sand-200 text-ink/55", w.status];
        const isOpen = open === w.id;
        return (
          <div key={w.id} className="mb-2 overflow-hidden rounded-2xl bg-white" data-testid={`vwo-row-${w.id}`}>
            <button onClick={() => setOpen(isOpen ? null : w.id)}
              className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}>{label}</span>
                  <span className="font-mono text-[11px] font-bold text-ink/35">{w.ref}</span>
                  {w.punch_open > 0 && <span className="text-[11px] font-bold text-amber-700">{w.punch_open} punch item(s)</span>}
                </div>
                <div className="mt-0.5 truncate text-sm font-bold text-ink">{w.title}</div>
                <div className="truncate text-xs text-ink/45">{w.community}{w.location ? ` · ${w.location}` : ""}</div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-sm font-bold text-ink">{usd(w.amount)}</span>
                <ChevronDown className={`h-4 w-4 text-ink/30 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </div>
            </button>
            {isOpen && <Job token={token} id={w.id} onChanged={load} />}
          </div>
        );
      })}
      <p className="mt-2 text-xs leading-relaxed text-ink/45">
        <CheckCircle2 className="mr-1 inline h-3.5 w-3.5 text-emerald-600" />
        Payment is handled by each community's own accounting. We'll tell you the moment an invoice is
        approved and again when it's paid.
      </p>
    </div>
  );
}
